import { FormArray, FormControl, FormGroup } from '@angular/forms';

import type { User } from '@recruitee/account-types';
import { FormGroupOf } from '@recruitee/common';
import type { SalaryData } from '@recruitee/offers-types';
import { EditorJSONState } from '@recruitee/prosemirror-types';
import { RequisitionOpeningReason } from '@recruitee/requisitions-types';
import type {
  ApprovalRequestData,
  Requisition,
  RequisitionColumnId,
  RequisitionOpeningStatus,
  RequisitionsGroupKind,
} from '@recruitee/requisitions-types';
import { DataTableColumn, SelectOption } from '@recruitee/user-interface';

export enum RequisitionsListTab {
  Active = 'active',
  Archived = 'archived',
}

export enum RequisitionsListViewType {
  Tiles = 'tiles',
  Table = 'table',
}

export type CachedRequisitionListViewState = {
  viewType: RequisitionsListViewType;
  groupBy: RequisitionsGroupKind | null;
  collapsedGroups: string[];
};

export type RequisitionsViewState = Pick<
  CachedRequisitionListViewState,
  'collapsedGroups' | 'viewType'
>;

export enum CreateMode {
  Blank = 'blank',
  Duplicate = 'duplicate',
}

export enum FormBox {
  Context = 'context',
  Details = 'details',
  Location = 'location',
  WorkModel = 'workModel',
  Openings = 'openings',
}

export type CreateRequisitionArgs =
  | {
      title: string;
      approvalFlowId: number;
    }
  | {
      title: string;
      departmentId: number | null;
    };

export interface OpeningControls {
  id: FormControl<number | null>;
  reason: FormControl<RequisitionOpeningReason | null>;
  anticipatedStartDate: FormControl<string | null>;
  explanation: FormControl<string | null>;
  status: FormControl<RequisitionOpeningStatus | null>;
  saved: FormControl<boolean>;
}

export interface WorkModelControls {
  hybrid: FormControl<boolean>;
  onSite: FormControl<boolean>;
  remote: FormControl<boolean>;
}

export const requisitionFieldsSelectOptions = {
  reasons: [RequisitionOpeningReason.NewPosition, RequisitionOpeningReason.Replacement],
};

export type AvailableFieldsMap = {
  [FormBox.Context]: Partial<{ [key in (typeof ContextFormFields)[number]]: boolean }>;
  [FormBox.Details]: Partial<{ [key in (typeof DetailsFormFields)[number]]: boolean }>;
  [FormBox.Location]: Partial<{ [key in (typeof LocationFormFields)[number]]: boolean }>;
  [FormBox.WorkModel]: Partial<{ [key in (typeof WorkModelFormFields)[number]]: boolean }>;
  [FormBox.Openings]: Partial<{ [key in (typeof OpeningsFormFields)[number]]: boolean }>;
};

export const ContextFormFields = ['general_context'];
export const DetailsFormFields = [
  'department',
  'recruiter',
  'hiring_manager',
  'employment_type',
  'category',
  'education',
  'experience',
  'hiring_budget',
  'hours',
  'salary',
];

export const LocationFormFields = ['locations'];
export const WorkModelFormFields = ['work_model'];
export const OpeningsFormFields = ['anticipated_start_date', 'reason', 'explanation'];

export type RequisitionForm = Partial<RequisitionFormControls>;

interface RequisitionFormControls {
  category: FormControl<string | null>;
  departmentId: FormControl<number | null>;
  education: FormControl<string | null>;
  employmentType: FormControl<string | null>;
  experience: FormControl<string | null>;
  generalContextJson: FormControl<EditorJSONState | object | null>;
  hiringBudget: FormControl<number | null>;
  hiringBudgetCurrency: FormControl<string | null>;
  hiringManagerId: FormControl<number | null>;
  minHoursPerWeek: FormControl<string | null>;
  maxHoursPerWeek: FormControl<string | null>;
  openings: FormArray<FormGroup<OpeningControls>>;
  locationIds: FormArray<FormControl<number>>;
  recruiterId: FormControl<number | null>;
  salary: FormGroupOf<SalaryData>;
  workModel: FormGroup<WorkModelControls>;
}

export type FlowSelectOption = SelectOption & { value: number };

export enum FlowStepViewContext {
  Page = 'page',
  Dialog = 'dialog',
}

export type ApprovalRequestWithRequester = ApprovalRequestData & { requester?: User };

export type RequisitionsGroupToDisplay = {
  name?: string;
  count?: number;
  requisitions: Requisition[];
};

export type RequisitionsTableColumn = DataTableColumn<RequisitionColumnId>;
